import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Button, Link, SxProps, Theme, Typography } from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ConfirmModal } from '~/components/ConfirmModal';
import { EmptyData } from '~/components/EmptyData';
import { GenerateLinkModal } from '~/components/GenerateLinkModal';
import { Pagination } from '~/components/Pagination';
import { ProgressBar } from '~/components/ProgressBar';
import { SearchInput } from '~/components/SearchInput';
import { useNotification } from '~/hooks/useNotification';
import { useDeactivateLinkMutation, useGetExternalUsersQuery } from '~/services/rtkQuery/admin';
import { LinkStatus } from '~/types/models';
import { formatLinkStatus, formatUrl } from '~/utils/formats';
import { generateLinkUrl } from '~/utils/helpers';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import { CopyIcon } from './components/CopyIcon';
import { Menu } from './components/Menu';
import { SelectCompanyModal } from './components/SelectCompanyModal';
import styles from './styles.module.scss';

export type FilterOption = LinkStatus | 'all';
const filterOptions: FilterOption[] = ['all', 'activated', 'deactivated', 'expired', 'submitted'];
const ICON_SX: SxProps<Theme> = { color: 'var(--gray-2-color)', width: '24px', height: '24px' };
const SEARCH_INPUT_SX: SxProps<Theme> = { mr: '12px', width: '344px' };
const FETCH_LIMIT = 10;

export const ExternalUsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get('search') || '';
  const statusParam = searchParams.get('status') as FilterOption;
  const status = useMemo(() => {
    if (!filterOptions.includes(statusParam)) {
      return 'all';
    }

    return statusParam;
  }, [statusParam]);
  const currentPage = +(searchParams.get('page') || 1);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [generateLinkModalVisible, setGenerateLinkModalVisible] = useState(false);
  const [selectCompanyModalVisible, setSelectCompanyModalVisible] = useState(false);
  const [deactivateLinkConfirmModalVisible, setDeactivateLinkConfirmModalVisible] = useState(false);
  const activeCompanyId = useRef<number | null>(null);
  const activeLinkId = useRef<number | null>(null);
  const { handleApiError, showNotification } = useNotification();
  const getExternalUsersQuery = useGetExternalUsersQuery({
    email: searchValue.trim(),
    status,
    pageNum: currentPage,
    perPage: FETCH_LIMIT,
  });
  const [deactivateLinkMutation, { isLoading: isLoadingDeactivateLinkMutation }] =
    useDeactivateLinkMutation();

  const handleSearchValueChange = useCallback(
    (value: string) => {
      setSearchParams(prevValue => {
        prevValue.set('search', value);
        prevValue.set('page', '1');

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const handleFilterOptionSelect = useCallback(
    (option: FilterOption) => {
      setSearchParams(prevValue => {
        prevValue.set('status', option);
        prevValue.set('page', '1');

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(prevValue => {
        prevValue.set('page', page.toString());

        return prevValue;
      });
    },
    [setSearchParams],
  );

  const showFilterMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setFilterAnchorEl(event.currentTarget);
  }, []);

  const hideFilterMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
      event.stopPropagation();

      setFilterAnchorEl(null);
    },
    [],
  );

  const getFilterOptionName = useCallback((option: FilterOption) => {
    if (option === 'all') {
      return 'All';
    }

    return formatLinkStatus(option);
  }, []);

  const showSelectCompanyModal = useCallback(() => {
    setSelectCompanyModalVisible(true);
  }, []);

  const hideSelectCompanyModal = useCallback(() => {
    setSelectCompanyModalVisible(false);
  }, []);

  const handleCompanySelect = useCallback((companyId: number) => {
    activeCompanyId.current = companyId;

    setGenerateLinkModalVisible(true);
  }, []);

  const showDeactivateLinkConfirmModal = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      linkId: number,
      companyId?: number,
    ) => {
      event.stopPropagation();

      activeLinkId.current = linkId;

      if (companyId) {
        activeCompanyId.current = companyId;
      } else {
        activeCompanyId.current = null;
      }

      setDeactivateLinkConfirmModalVisible(true);
    },
    [],
  );

  const hideDeactivateLinkConfirmModal = useCallback(() => {
    setDeactivateLinkConfirmModalVisible(false);
  }, []);

  const handleDeactivateLinkConfirm = useCallback(async () => {
    try {
      if (activeLinkId.current) {
        const response = await deactivateLinkMutation({
          id: activeLinkId.current,
        }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
        });

        hideDeactivateLinkConfirmModal();

        if (activeCompanyId.current) {
          setGenerateLinkModalVisible(true);
        }
      }
    } catch (e) {
      handleApiError(e);
    }
  }, [deactivateLinkMutation, handleApiError, hideDeactivateLinkConfirmModal, showNotification]);

  const showGenerateLinkModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, companyId: number) => {
      event.stopPropagation();

      activeCompanyId.current = companyId;

      setGenerateLinkModalVisible(true);
    },
    [],
  );

  const hideGenerateLinkModal = useCallback(() => {
    setGenerateLinkModalVisible(false);
  }, []);

  const totalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: getExternalUsersQuery.data?.result.total || 0,
      fetchLimit: FETCH_LIMIT,
    });
  }, [getExternalUsersQuery.data?.result.total]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.container__heading}>
          <Typography color="var(--text-2-color)" fontWeight="700" variant="subtitle1">
            Companies List
          </Typography>
          <div className={styles['container__right-column']}>
            <button
              aria-label="Filter"
              className={styles['container__filter-button']}
              title="Filter"
              type="button"
              onClick={showFilterMenu}
            >
              <Typography color="var(--black-color)" variant="subtitle1">
                {getFilterOptionName(status)}
              </Typography>
              {filterAnchorEl ? (
                <ArrowDropUpIcon sx={ICON_SX} />
              ) : (
                <ArrowDropDownIcon sx={ICON_SX} />
              )}
            </button>
            <SearchInput
              initialValue={searchValue}
              placeholder="by Email"
              sx={SEARCH_INPUT_SX}
              onChange={handleSearchValueChange}
            />
            <Button
              classes={{ startIcon: styles['container__generate-button-icon'] }}
              color="secondary"
              size="small"
              startIcon={<LinkOutlinedIcon sx={{ ...ICON_SX, color: 'var(--main-1-color)' }} />}
              sx={{
                width: '150px',
                paddingLeft: '6px',
                paddingRight: '6px',
                '&:hover': {
                  color: 'var(--white-color)',
                  backgroundColor: 'var(--main-1-color)',
                  '& .MuiButton-startIcon svg': {
                    color: 'var(--white-color)',
                  },
                },
              }}
              onClick={showSelectCompanyModal}
            >
              Generate Link
            </Button>
          </div>
        </div>
        {getExternalUsersQuery.isFetching ||
        !getExternalUsersQuery.data ||
        getExternalUsersQuery.data.result.data.length === 0 ? (
          <EmptyData
            className={styles['container__empty-data']}
            description="No users available"
            loading={getExternalUsersQuery.isFetching}
          />
        ) : (
          <>
            <table className={styles.container__table}>
              <thead>
                <tr>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Company Name
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Website Link
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Email
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Latest Link
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Status
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Progress
                    </Typography>
                  </th>
                  <th>
                    <Typography
                      color="var(--text-3-color)"
                      fontWeight="300"
                      lineHeight="16px"
                      variant="body1"
                    >
                      Actions
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {getExternalUsersQuery.data?.result.data.map(user => (
                  <tr key={user.id}>
                    <td>
                      <Typography
                        color="text.primary"
                        fontWeight="500"
                        lineHeight="16px"
                        sx={{ textDecoration: 'underline' }}
                        variant="body1"
                      >
                        {user.company_data.company_name}
                      </Typography>
                    </td>
                    <td>
                      <Link
                        color="rgb(var(--main-2-color-rgb))"
                        fontWeight="500"
                        href={formatUrl(user.company_data.website_url)}
                        lineHeight="20px"
                        target="_blank"
                        variant="body1"
                      >
                        {user.company_data.website_url}
                      </Link>
                    </td>
                    <td>
                      <Typography
                        color="text.primary"
                        fontWeight="500"
                        lineHeight="16px"
                        variant="body1"
                      >
                        {user.email}
                      </Typography>
                    </td>
                    <td>
                      <div className={styles['container__copy-link']}>
                        <CopyIcon text={generateLinkUrl(user.uuid, user.company_data.company_id)} />
                        <Link
                          color="rgb(var(--main-2-color-rgb))"
                          fontWeight="500"
                          href={generateLinkUrl(user.uuid, user.company_data.company_id)}
                          lineHeight="20px"
                          sx={{ textDecorationLine: 'none' }}
                          target="_blank"
                          variant="body1"
                        >
                          {user.company_data.company_name}
                        </Link>
                      </div>
                    </td>
                    <td>
                      <Typography
                        color="var(--gray-2-color)"
                        fontWeight="500"
                        lineHeight="16px"
                        variant="body1"
                      >
                        {formatLinkStatus(user.external_user_status)}
                      </Typography>
                    </td>
                    <td>
                      <ProgressBar
                        className={styles['container__progress-bar']}
                        label="Profile Completed"
                        value={user.company_data.progress_bar * 100}
                      />
                    </td>
                    <td>
                      <div>
                        <button
                          aria-label="Generate Link"
                          title="Generate Link"
                          type="button"
                          onClick={event =>
                            user.external_user_status === 'activated'
                              ? showDeactivateLinkConfirmModal(
                                  event,
                                  user.id,
                                  user.company_data.company_id,
                                )
                              : showGenerateLinkModal(event, user.company_data.company_id)
                          }
                        >
                          <AddLinkOutlinedIcon sx={ICON_SX} />
                        </button>
                        {user.external_user_status !== 'deactivated' &&
                          user.external_user_status !== 'submitted' && (
                            <button
                              aria-label="Deactivate Link"
                              title="Deactivate Link"
                              type="button"
                              onClick={event => showDeactivateLinkConfirmModal(event, user.id)}
                            >
                              <LinkOffIcon sx={{ ...ICON_SX, color: 'var(--error-color)' }} />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.container__footer}>
              <Typography color="var(--gray-2-color)" lineHeight="14px" variant="subtitle2">
                {getCurrentPageInfo({
                  fetchLimit: FETCH_LIMIT,
                  currentPage,
                  currentPageItemsCount: getExternalUsersQuery.data?.result.data.length || 0,
                  totalItemsCount: getExternalUsersQuery.data?.result.total || 0,
                })}
              </Typography>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
      <Menu
        anchorEl={filterAnchorEl}
        filterOptions={filterOptions}
        getFilterOptionName={getFilterOptionName}
        onClose={hideFilterMenu}
        onSelect={handleFilterOptionSelect}
      />
      <ConfirmModal
        description={
          activeCompanyId.current
            ? 'An active link exists and will be deactivated. Do you want to proceed?'
            : 'Are you sure you want to deactivate this link? The external user will no longer have access to edit or submit the company profile.'
        }
        loading={isLoadingDeactivateLinkMutation}
        open={deactivateLinkConfirmModalVisible}
        title={activeCompanyId.current ? 'Deactivate link' : 'Confirm Deactivation'}
        onCancel={hideDeactivateLinkConfirmModal}
        onConfirm={handleDeactivateLinkConfirm}
      />
      {selectCompanyModalVisible && (
        <SelectCompanyModal
          open={selectCompanyModalVisible}
          onClose={hideSelectCompanyModal}
          onCompanySelect={handleCompanySelect}
        />
      )}
      {generateLinkModalVisible && (
        <GenerateLinkModal
          companyId={activeCompanyId.current as number}
          open={generateLinkModalVisible}
          onClose={hideGenerateLinkModal}
        />
      )}
    </>
  );
};
